import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/*
  Theme system
  ───────────
  The platform now supports three explicit user choices:

    • light   → force light mode
    • dark    → force dark mode
    • system  → follow the OS / browser `prefers-color-scheme`

  Back-compat: older builds stored the resolved theme ('light' | 'dark') at the
  same key. We migrate transparently on load — any non-'system' value is kept
  as the explicit choice.

  We keep TWO pieces of state:
    • themePreference  → what the user picked                (persisted)
    • theme            → the currently *applied* theme        (derived)

  Components that only care about what to paint still use `theme` — no change
  required to existing call sites.
*/

const ThemeContext = createContext();
const STORAGE_KEY = 'ccuk-theme';
const VALID_PREFS = ['light', 'dark', 'system'];

function prefersLight() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: light)').matches
  );
}

function resolveTheme(pref) {
  if (pref === 'light' || pref === 'dark') return pref;
  // 'system' (or anything unexpected) → follow the OS
  return prefersLight() ? 'light' : 'dark';
}

export function ThemeProvider({ children }) {
  // Initial preference — load before the first paint using useState lazy init
  // so the <html> class gets set in the same synchronous tick, preventing a
  // flash of the wrong theme on refresh.
  const [themePreference, setThemePreferenceState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && VALID_PREFS.includes(stored)) return stored;
    } catch {
      /* localStorage blocked — fall through to default */
    }
    // Default for first-time visitors: light mode. (Was 'system' before —
    // users who explicitly picked dark / system / light still keep their
    // saved preference via the localStorage check above.)
    return 'light';
  });

  const [theme, setTheme] = useState(() => resolveTheme(themePreference));

  // Apply the resolved theme class to <html> whenever either the preference
  // changes, or (if following system) the OS preference changes.
  useEffect(() => {
    const resolved = resolveTheme(themePreference);
    setTheme(resolved);
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolved);
    root.dataset.themePreference = themePreference;
    try {
      localStorage.setItem(STORAGE_KEY, themePreference);
    } catch {
      /* ignore quota / privacy mode */
    }
  }, [themePreference]);

  // Listen for OS changes only when the user picked 'system'. Otherwise the
  // user has made an explicit choice and we leave it alone.
  useEffect(() => {
    if (themePreference !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => {
      const resolved = resolveTheme('system');
      setTheme(resolved);
      const root = document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(resolved);
    };
    // Cross-browser event wiring (older Safari uses addListener)
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, [themePreference]);

  // Public API ──────────────────────────────────────────────────────────
  // setThemePreference('light' | 'dark' | 'system')  → set the explicit pick
  // setTheme('light' | 'dark')                       → legacy shim, also sets the pick
  // toggleTheme()                                    → legacy shim, flips light ↔ dark
  const setThemePreference = (next) => {
    if (!VALID_PREFS.includes(next)) return;
    setThemePreferenceState(next);
  };

  const legacySetTheme = (next) => {
    if (next === 'light' || next === 'dark') setThemePreferenceState(next);
  };

  const toggleTheme = () => {
    // Cycle between light and dark regardless of whether the current pref is
    // 'system' — this keeps the header single-button toggle behaving exactly
    // as it did before. The explicit 'system' option now lives in the user
    // dropdown's "Appearance" sub-menu, not on the header button.
    setThemePreferenceState((prev) => {
      const currentlyDark = resolveTheme(prev) === 'dark';
      return currentlyDark ? 'light' : 'dark';
    });
  };

  const value = useMemo(
    () => ({
      theme,
      themePreference,
      setTheme: legacySetTheme,
      setThemePreference,
      toggleTheme,
    }),
    [theme, themePreference]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
